"use client";

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useContext,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { reduxReset } from "redux/store";

type AuthUser = {
  user_id?: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
};

type LoginCredentials = {
  identifier: string;
  password: string;
};

type AuthFetchInput = Parameters<typeof fetch>[0];
type AuthFetchInit = Parameters<typeof fetch>[1];

export type AuthContextValue = {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  actorPermissions: string[];
  loading: boolean;
  /* eslint-disable-next-line no-unused-vars */
  login: (credentials: LoginCredentials) => Promise<void>;
  logout(): Promise<void>;
  /* eslint-disable-next-line no-unused-vars */
  authFetch: (input: AuthFetchInput, init?: AuthFetchInit) => Promise<Response>;
  authError: string | null;
  setAuthError: (_error: string | null) => void;
  reloadActorPermissions: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}

const ACCESS_TOKEN_KEY = "iam.access_token";
const REFRESH_TOKEN_KEY = "iam.refresh_token";
const USER_KEY = "iam.user";
const DEVICE_ID_KEY = "iam.device_id";

type LoginResponse = {
  access_token?: string;
  refresh_token?: string;
  user?: AuthUser;
  token_type?: string;
  expires_in?: number;
  refresh_expires_in?: number;
  [key: string]: any;
};

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [actorPermissions, setActorPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const refreshPromiseRef = useRef<Promise<{
    ok: boolean;
    payload?: any;
  } | null> | null>(null);
  const forcePermissionRefreshUntilRef = useRef<number>(0);
  const userRef = useRef<AuthUser | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const refreshTokenRef = useRef<string | null>(null);

  const getOrCreateDeviceId = useCallback(() => {
    if (typeof window === "undefined") return null;
    const existing = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existing && existing.trim()) return existing.trim();

    const generated =
      window.crypto && typeof window.crypto.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(DEVICE_ID_KEY, generated);
    return generated;
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const getJwtExpiryMs = useCallback((token: string | null): number | null => {
    if (!token || token.split(".").length !== 3) return null;
    try {
      const payloadB64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(payloadB64));
      const exp = Number(payload?.exp);
      if (!Number.isFinite(exp) || exp <= 0) return null;
      return exp * 1000;
    } catch {
      return null;
    }
  }, []);

  const isTokenExpiredOrNearExpiry = useCallback(
    (token: string | null, skewMs = 120_000) => {
      const expiryMs = getJwtExpiryMs(token);
      if (!expiryMs) return false;
      return Date.now() >= expiryMs - skewMs;
    },
    [getJwtExpiryMs],
  );

  useEffect(() => {
    const storedAccess =
      typeof window !== "undefined"
        ? window.localStorage.getItem(ACCESS_TOKEN_KEY)
        : null;
    const storedRefresh =
      typeof window !== "undefined"
        ? window.localStorage.getItem(REFRESH_TOKEN_KEY)
        : null;
    const storedUser =
      typeof window !== "undefined"
        ? window.localStorage.getItem(USER_KEY)
        : null;

    accessTokenRef.current = storedAccess;
    refreshTokenRef.current = storedRefresh;
    if (storedAccess) setAccessToken(storedAccess);
    if (storedRefresh) setRefreshToken(storedRefresh);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (err) {
        console.error("Failed to parse stored user", err);
      }
    }

    // If this tab was opened via browser reload, force a short-lived
    // permission-cache bypass on initial API requests.
    try {
      if (typeof window !== "undefined" && typeof performance !== "undefined") {
        const nav = performance.getEntriesByType("navigation");
        const navType = nav && nav[0] && (nav[0] as any).type;
        if (navType === "reload") {
          forcePermissionRefreshUntilRef.current = Date.now() + 15_000;
        }
      }
    } catch {
      // ignore performance API issues
    }

    setLoading(false);
  }, []);

  const persistSession = useCallback((payload: LoginResponse) => {
    const nextAccess = payload.access_token ?? null;
    const nextRefresh = payload.refresh_token ?? null;
    const nextUser =
      payload.user === undefined ? userRef.current : payload.user ?? null;

    setAccessToken(nextAccess);
    setRefreshToken(nextRefresh);
    setUser(nextUser);
    accessTokenRef.current = nextAccess;
    refreshTokenRef.current = nextRefresh;

    if (typeof window !== "undefined") {
      if (nextAccess) {
        window.localStorage.setItem(ACCESS_TOKEN_KEY, nextAccess);
      } else {
        window.localStorage.removeItem(ACCESS_TOKEN_KEY);
      }

      if (nextRefresh) {
        window.localStorage.setItem(REFRESH_TOKEN_KEY, nextRefresh);
      } else {
        window.localStorage.removeItem(REFRESH_TOKEN_KEY);
      }

      if (nextUser) {
        window.localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
      } else {
        window.localStorage.removeItem(USER_KEY);
      }
    }
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    accessTokenRef.current = null;
    refreshTokenRef.current = null;
    setActorPermissions([]);
    reduxReset();
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(ACCESS_TOKEN_KEY);
      window.localStorage.removeItem(REFRESH_TOKEN_KEY);
      window.localStorage.removeItem(USER_KEY);
    }
  }, []);

  const loadActorPermissions = useCallback(
    async (
      currentUser: AuthUser | null,
      currentAccessToken: string | null,
      currentRefreshToken: string | null,
    ) => {
      const actorUserId = currentUser?.user_id;
      if (!actorUserId || !currentAccessToken) {
        setActorPermissions([]);
        return;
      }

      try {
        const headers = new Headers({ accept: "application/json" });
        headers.set("authorization", `Bearer ${currentAccessToken}`);
        if (currentRefreshToken) {
          headers.set("x-refresh-token", currentRefreshToken);
        }
        const requestId =
          typeof window !== "undefined" &&
          window.crypto &&
          typeof window.crypto.randomUUID === "function"
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        headers.set("x-request-id", requestId);
        headers.set("x-correlation-id", requestId);
        headers.set("x-client-application", "iam-user-ui");

        const response = await fetch(
          `/api/users/${encodeURIComponent(actorUserId)}/permissions`,
          {
            headers,
            credentials: "same-origin",
          },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          setActorPermissions([]);
          return;
        }
        const permissions = Array.isArray(payload?.permissions)
          ? payload.permissions
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        setActorPermissions(
          permissions
            .map((permission: any) =>
              String(permission?.name || permission?.permission || permission || ""),
            )
            .filter(Boolean),
        );
      } catch {
        setActorPermissions([]);
      }
    },
    [],
  );

  const login = useCallback(
    async ({ identifier, password }: LoginCredentials) => {
      setLoading(true);
      try {
        const normalized = identifier.trim();
        const defaultDomain =
          process.env.NEXT_PUBLIC_DEFAULT_EMAIL_DOMAIN || "origin-north.ai";
        const email = normalized.includes("@")
          ? normalized
          : `${normalized}@${defaultDomain}`;
        const loginPayload = { email, password, username: normalized };
        const deviceId = getOrCreateDeviceId();

        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: {
            "content-type": "application/json",
            ...(deviceId ? { "x-device-id": deviceId } : {}),
          },
          body: JSON.stringify(loginPayload),
          credentials: "same-origin",
        });

        const responsePayload: LoginResponse = await res
          .json()
          .catch(() => ({}));
        if (!res.ok || !responsePayload.access_token) {
          const message =
            responsePayload.message ||
            responsePayload.error ||
            "Invalid credentials";
          throw new Error(message);
        }

        persistSession(responsePayload);

        // After persisting tokens, verify access for the current application
        // against the required entity and app-scoped iam:access permission.
        try {
          const meRes = await fetch("/api/auth/access", {
            credentials: "same-origin",
            headers: {
              authorization: `Bearer ${responsePayload.access_token}`,
            },
          });
          if (!meRes || !meRes.ok) {
            // treat inability to read profile as denied
            clearSession();
            if (typeof window !== "undefined")
              window.location.replace("/access-denied");
            return;
          }
          const mePayload = await meRes.json().catch(() => null);
          const allowed = Boolean((mePayload as any)?.allowed);
          if (!allowed) {
            clearSession();
            if (typeof window !== "undefined")
              window.location.replace("/access-denied");
            return;
          }
          if (mePayload?.user) {
            persistSession({ ...responsePayload, user: mePayload.user });
          }
        } catch (err) {
          console.warn("Failed to fetch profile after login", err);
          clearSession();
          if (typeof window !== "undefined")
            window.location.replace("/access-denied");
          return;
        }
      } finally {
        setLoading(false);
      }
    },
    [persistSession, clearSession, getOrCreateDeviceId],
  );

  const logout = useCallback(async () => {
    if (!refreshToken) {
      clearSession();
      return;
    }

    try {
      const deviceId = getOrCreateDeviceId();
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(deviceId ? { "x-device-id": deviceId } : {}),
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: "same-origin",
      });
    } catch (err) {
      console.warn("Failed to notify logout", err);
    } finally {
      clearSession();
    }
  }, [clearSession, refreshToken, getOrCreateDeviceId]);

  const refreshSession = useCallback(async (): Promise<{
    ok: boolean;
    payload?: any;
  }> => {
    if (refreshPromiseRef.current) {
      const existing = await refreshPromiseRef.current;
      return existing || { ok: false };
    }

    const refreshPromise = (async (): Promise<{
      ok: boolean;
      payload?: any;
    }> => {
      try {
        const refreshHeaders: Record<string, string> = {
          "content-type": "application/json",
        };
        const currentRefreshToken =
          refreshTokenRef.current ||
          (typeof window !== "undefined"
            ? window.localStorage.getItem(REFRESH_TOKEN_KEY)
            : null);
        if (!currentRefreshToken) return { ok: false };
        refreshHeaders["x-refresh-token"] = currentRefreshToken;
        const requestId =
          typeof window !== "undefined" &&
          window.crypto &&
          typeof window.crypto.randomUUID === "function"
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        refreshHeaders["x-request-id"] = requestId;
        refreshHeaders["x-correlation-id"] = requestId;
        refreshHeaders["x-client-application"] = "iam-user-ui";
        const deviceId = getOrCreateDeviceId();
        if (deviceId) refreshHeaders["x-device-id"] = deviceId;
        try {
          const nav =
            typeof window !== "undefined" ? (window.navigator as any) : null;
          const platformHint = nav?.userAgentData?.platform || nav?.platform || "";
          if (platformHint) refreshHeaders["x-client-os"] = String(platformHint);
        } catch {
          // best-effort only
        }
        const appCode = process.env.NEXT_PUBLIC_APPLICATION_CODE || "IAM";
        if (appCode) refreshHeaders["x-application-code"] = appCode;
        const refreshRes = await fetch("/api/auth/refresh", {
          method: "POST",
          headers: refreshHeaders,
          body: JSON.stringify({ refresh_token: currentRefreshToken }),
          credentials: "same-origin",
        });
        const refreshPayload = await refreshRes.json().catch(() => ({}));
        if (refreshRes.ok && refreshPayload.access_token) {
          persistSession(refreshPayload);
          return { ok: true, payload: refreshPayload };
        }
        if (refreshPayload?.error === "refresh_token_reuse_detected") {
          clearSession();
          if (typeof window !== "undefined") {
            window.location.replace("/login?reason=session_revoked");
          }
        }
        return { ok: false, payload: refreshPayload };
      } catch (err) {
        console.debug("refresh attempt failed", err);
        return { ok: false };
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = refreshPromise;
    const result = await refreshPromise;
    return result || { ok: false };
  }, [clearSession, persistSession, getOrCreateDeviceId]);

  const authFetch = useCallback(
    async (input: AuthFetchInput, init?: AuthFetchInit) => {
      const initWithDefaults = init ?? {};
      const existingHeaders = (initWithDefaults as Record<string, unknown>)
        .headers;
      const headers = new Headers(existingHeaders as any);
      const suppressAuthRedirect = headers.get("x-auth-soft-fail") === "1";

      // State updates from session hydration/token rotation are asynchronous.
      // Refs are updated synchronously, while localStorage covers the first
      // render after a hard browser refresh.
      let requestAccessToken =
        accessTokenRef.current ||
        accessToken ||
        (typeof window !== "undefined"
          ? window.localStorage.getItem(ACCESS_TOKEN_KEY)
          : null);
      let requestRefreshToken =
        refreshTokenRef.current ||
        refreshToken ||
        (typeof window !== "undefined"
          ? window.localStorage.getItem(REFRESH_TOKEN_KEY)
          : null);

      if (requestRefreshToken) {
        if (refreshPromiseRef.current) {
          const refreshed = await refreshPromiseRef.current;
          if (refreshed?.ok && refreshed.payload?.access_token) {
            requestAccessToken = refreshed.payload.access_token;
            requestRefreshToken = refreshed.payload.refresh_token ?? requestRefreshToken;
          }
        } else if (isTokenExpiredOrNearExpiry(requestAccessToken)) {
          const refreshed = await refreshSession();
          if (refreshed?.ok && refreshed.payload?.access_token) {
            requestAccessToken = refreshed.payload.access_token;
            requestRefreshToken = refreshed.payload.refresh_token ?? requestRefreshToken;
          }
        }
      }

      if (requestAccessToken && !headers.has("authorization")) {
        headers.set("authorization", `Bearer ${requestAccessToken}`);
      }
      if (requestRefreshToken && !headers.has("x-refresh-token")) {
        headers.set("x-refresh-token", requestRefreshToken);
      }
      if (!headers.has("x-request-id")) {
        const requestId =
          typeof window !== "undefined" &&
          window.crypto &&
          typeof window.crypto.randomUUID === "function"
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        headers.set("x-request-id", requestId);
        if (!headers.has("x-correlation-id"))
          headers.set("x-correlation-id", requestId);
      }
      if (!headers.has("x-client-application")) {
        headers.set("x-client-application", "iam-user-ui");
      }
      if (!headers.has("x-device-id")) {
        const deviceId = getOrCreateDeviceId();
        if (deviceId) headers.set("x-device-id", deviceId);
      }
      if (!headers.has("x-client-os")) {
        try {
          const nav =
            typeof window !== "undefined" ? (window.navigator as any) : null;
          const platformHint =
            nav?.userAgentData?.platform || nav?.platform || "";
          if (platformHint) headers.set("x-client-os", String(platformHint));
        } catch {
          // best-effort only
        }
      }
      if (!headers.has("x-application-code")) {
        const appCode = process.env.NEXT_PUBLIC_APPLICATION_CODE || "IAM";
        if (appCode) headers.set("x-application-code", appCode);
      }
      if (Date.now() < forcePermissionRefreshUntilRef.current) {
        headers.set("x-force-permission-refresh", "1");
      }
      const credentials =
        (initWithDefaults as any).credentials ?? "same-origin";

      const doFetch = (hdrs: Headers) =>
        fetch(input, { ...initWithDefaults, headers: hdrs, credentials });

      // safeDoFetch wraps fetch to normalize network errors (DNS, ECONNREFUSED, etc.)
      const safeDoFetch = async (hdrs: Headers) => {
        try {
          return await doFetch(hdrs);
        } catch (err: any) {
          // Return a Response-like object so callers can always inspect status / json()
          const body = JSON.stringify({ error: String(err?.message || err) });
          return new Response(body, {
            status: 502,
            headers: { "content-type": "application/json" },
          });
        }
      };

      let res = await safeDoFetch(headers);

      // If auth failed due to an invalid/expired token (401), attempt a
      // single refresh+retry when a refresh token exists. Do NOT attempt a
      // refresh for 403 (access denied) since that indicates insufficient
      // permissions rather than an expired/invalid session.
      if (res.status === 401 && requestRefreshToken) {
        try {
          const r = await refreshSession();
          if (r && r.ok && r.payload?.access_token) {
            const retryHeaders = new Headers(headers);
            retryHeaders.set("authorization", `Bearer ${r.payload.access_token}`);
            if (r.payload.refresh_token) {
              retryHeaders.set("x-refresh-token", r.payload.refresh_token);
            }
            res = await safeDoFetch(retryHeaders);
            if (res.status !== 401 && res.status !== 403) return res;
          } else {
            if (!suppressAuthRedirect) {
              clearSession();
              if (typeof window !== "undefined")
                window.location.replace("/login?reason=session_revoked");
            }
            return res;
          }
        } catch (err) {
          console.debug("refresh attempt failed", err);
          // fallthrough to clear session
        }
      }

      // If we get here and the response indicates the token is invalid/expired
      // (401) clear the session and surface an error. For 403 (access denied)
      // we should NOT clear the session or redirect — keep the user signed in
      // and show a friendly access-denied message.
      if (res.status === 401) {
        if (!suppressAuthRedirect) {
          clearSession();
          setAuthError("Your session has expired. Please sign in again.");
          if (typeof window !== "undefined") {
            // Redirect the user to the login page so they are not left on a
            // partially-loaded page. Preserve a reason query for diagnostics.
            try {
              window.location.replace("/login?reason=session_expired");
            } catch {
              // ignore navigation errors
            }
          }
        }
      } else if (res.status === 403) {
        let payload: any = null;
        try {
          payload = await res.clone().json();
        } catch {
          payload = null;
        }
        if (
          payload?.redirect_to &&
          (payload?.required_entity_id || payload?.application_code)
        ) {
          setAuthError("Access is denied");
          if (typeof window !== "undefined") {
            try {
              window.location.replace(payload?.redirect_to || "/access-denied");
            } catch {
              // ignore navigation errors
            }
          }
          return res;
        }
        setAuthError("Access is denied");
      }

      return res;
    },
    [accessToken, refreshToken, clearSession, refreshSession, isTokenExpiredOrNearExpiry, getOrCreateDeviceId],
  );

  // Refresh shortly before expiry even when the user is idle. authFetch also
  // retains its request-time refresh-and-retry protection for background tabs
  // where browsers may throttle this timer.
  useEffect(() => {
    if (loading || !refreshToken) return;
    const expiryMs = getJwtExpiryMs(accessToken);
    if (!expiryMs) return;

    const refreshInMs = Math.max(0, expiryMs - Date.now() - 120_000);
    const timer = window.setTimeout(() => {
      void refreshSession();
    }, refreshInMs);
    return () => window.clearTimeout(timer);
  }, [accessToken, refreshToken, loading, getJwtExpiryMs, refreshSession]);

  useEffect(() => {
    if (loading) return;
    if (!user || !accessToken) {
      setActorPermissions([]);
      return;
    }
    void loadActorPermissions(user, accessToken, refreshToken);
  }, [user, accessToken, refreshToken, loading, loadActorPermissions]);

  const value = useMemo(
    () => ({
      user,
      accessToken,
      refreshToken,
      actorPermissions,
      loading,
      login,
      logout,
      authFetch,
      authError,
      setAuthError,
      reloadActorPermissions: async () => {
        await loadActorPermissions(user, accessToken, refreshToken);
      },
    }),
    [
      user,
      accessToken,
      refreshToken,
      actorPermissions,
      loading,
      login,
      logout,
      authFetch,
      authError,
      loadActorPermissions,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Global toast for auth errors */}
      {authError && (
        <Snackbar
          open={!!authError}
          autoHideDuration={6000}
          onClose={() => setAuthError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error" onClose={() => setAuthError(null)}>
            {authError}
          </Alert>
        </Snackbar>
      )}
    </AuthContext.Provider>
  );
}
